import { ActionTree } from 'vuex';
import { Campus, CampusWorkspace, City, LocationState } from './state';
import RootState from '@/store/root_state';
import { query, MicroserviceEnum } from '@/helpers/westeros';
import {
  allCampus,
  AllCampusQueryResult,
  allCities,
  AllCitiesQueryResult,
  campusInfo,
  CampusInfoQueryResult,
} from '@/store/graphql/queries/westerosQueries';
import { UpsertAdmissionMutationInput } from '@/store/graphql/mutations/westerosMutations';
import { readSelectedCity, saveSelectedCity } from '@/helpers/selectedCityStorage';

const actions: ActionTree<LocationState, RootState> = {
  async UPDATE_CITY({ commit, rootGetters }, payload: { city: City }): Promise<void> {
    const { city } = payload;

    // O backend não tem campo de cidade (só `campusId`), então a escolha só existe no
    // store — que nasce vazio a cada reload. Espelhar em sessionStorage é o que permite
    // reidratar em /selecao-de-polo, onde ainda não há polo para derivar a cidade.
    saveSelectedCity(city, rootGetters.admissionId);

    commit('SET_CITY', city);
    commit('SET_AVAILABLE_CAMPUSES', []);
  },

  // Reidrata a cidade no boot. Vai no App.vue entre PULL_CURRENT_ADMISSION e
  // START_PIPELINE: a primeira navegação do browser acontece com o pipeline ainda não
  // inicializado e cai no next('/'); é o START_PIPELINE que produz a navegação em que o
  // hasCityInStoreMiddleware de fato avalia a cidade.
  async RESTORE_SELECTED_CITY({ commit, state, rootGetters }): Promise<void> {
    // cidade vinda do polo já gravado é mais confiável que o registro de sessão.
    if (state.city) return;

    const city = readSelectedCity(rootGetters.admissionId);

    if (city) commit('SET_CITY', city);
  },

  async UPDATE_CAMPUS({ dispatch }, payload: { campus: Campus }): Promise<void> {
    const { campus } = payload;

    // sem id o JSON.stringify da mutation dropa a chave, o upsert roda com
    // `data: {}` e o backend responde 200 sem gravar polo nenhum — falha
    // silenciosa que deixa o candidato avançar sem polo.
    if (!campus?.id) {
      throw new Error('UPDATE_CAMPUS: campus sem id');
    }

    const params: UpsertAdmissionMutationInput = { campusId: campus.id };

    await dispatch('UPSERT_ADMISSION', params);
  },

  async PULL_AVAILABLE_CITIES({ commit }): Promise<void> {
    const { data } = await query<AllCitiesQueryResult>(
      MicroserviceEnum.ADMISSIONS,
      allCities,
      { isAvailable: true },
      true,
    );

    const cities: City[] = data.allCities;

    commit(
      'SET_AVAILABLE_CITIES',
      cities.sort(({ city: city1 = '' }, { city: city2 = '' }) => city1.localeCompare(city2)),
    );
  },

  async PULL_AVAILABLE_CAMPUSES({ commit, state }): Promise<void> {
    const { city } = state;

    const { data } = await query<AllCampusQueryResult>(
      MicroserviceEnum.ADMISSIONS,
      allCampus,
      {
        city: city?.city,
        state: city?.state,
        isAvailable: true,
      },
      true,
    );

    const campuses: Campus[] = data.allCampus;

    commit(
      'SET_AVAILABLE_CAMPUSES',
      campuses.sort(({ name: name1 = '' }, { name: name2 = '' }) => name1.localeCompare(name2)),
    );
  },
  async PULL_CAMPUS_DETAILS({ commit, state }, payload: { campus: Campus }): Promise<void> {
    const { campus } = payload;

    const { data } = await query<CampusInfoQueryResult>(
      MicroserviceEnum.ADMISSIONS,
      campusInfo,
      { id: campus.id },
      true,
    );

    // O item da lista é a fonte confiável do `id` (a query de detalhe alimenta
    // só exibição). Mesclar os dois faz o objeto que chega no modal ser um
    // Campus de verdade, e não um DTO de exibição sem id — que é o que fazia o
    // "Escolher esse polo" do modal salvar admissão sem polo.
    const selectedCampus = state.availableCampuses?.find(({ id }) => id === campus.id) || campus;

    const campusDetails: Campus = {
      ...selectedCampus,
      id: data.campusInfo.id ?? selectedCampus.id,
      name: data.campusInfo.name,
      address: data.campusInfo.address,
      description: data.campusInfo.description,
      neighborhood: data.campusInfo.neighborhood,
      photos: data.campusInfo.campusPhotos.map(({ photoUrl }) => photoUrl),
      ...data.campusInfo.campusAmenities.reduce(
        (partialCampus, { title = '', description = '', photoUrl = '', amenityType }) => {
          if (amenityType) partialCampus.amenities.push(amenityType);
          else partialCampus.workspaces.push({ title, description, image: photoUrl });

          return partialCampus;
        },
        { workspaces: [], amenities: [] } as { workspaces: CampusWorkspace[]; amenities: string[] },
      ),
    };

    commit('SET_CAMPUS_DETAILS', campusDetails);
  },
};

export default actions;
